import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import DataTable from '../../../sharedModules/dataTable/DataTable/DataTable';
import { IDataTableColumn } from '../../../sharedModules/dataTable/DataTable/model';
import axios from '../../../sharedModules/utils/axios.utils';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';

const invoiceColumns: IDataTableColumn<any>[] = [
  {
    name: 'Location',
    selector: 'location',
    sortable: true,
    width: '120px',
    format: (row: any) => row.location,
  },
  {
    name: 'Vendor ID',
    selector: 'vendor_id',
    sortable: true,
    width: '120px',
    format: (row: any) => row.vendor_id,
  },
  {
    name: 'Vendor Name',
    selector: 'vendor_name',
    sortable: true,
    grow: 2,
    format: (row: any) => row.vendor_name || 'N/A',
  },
  {
    name: 'Invoice Number',
    selector: 'invoice_number',
    sortable: true,
    width: '150px',
    format: (row: any) => row.invoice_number,
  },
  {
    name: 'Invoice Date',
    selector: 'invoice_date',
    sortable: true,
    width: '130px',
    format: (row: any) => (row.invoice_date ? new Date(row.invoice_date).toLocaleDateString() : 'N/A'),
  },
  {
    name: 'Amount',
    selector: 'invoice_amount',
    sortable: true,
    width: '130px',
    format: (row: any) =>
      row.invoice_amount
        ? `$${parseFloat(row.invoice_amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
        : 'N/A',
  },
  {
    name: 'Tran Type',
    selector: 'trantype',
    sortable: true,
    width: '100px',
    format: (row: any) => row.trantype || 'N/A',
  },
  {
    name: 'Synced At',
    selector: 'synced_at',
    sortable: true,
    width: '180px',
    format: (row: any) => (row.synced_at ? new Date(row.synced_at).toLocaleString() : 'N/A'),
  },
];

const ServmanInvoicesListPage: React.FC = () => {
  const [invoices, setInvoices] = useState<any[]>([]);
  const [vendorNames, setVendorNames] = useState<string[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [sort, setSort] = useState<string>('synced_at desc');
  const [searchInvoiceNumber, setSearchInvoiceNumber] = useState('');
  const [selectedVendor, setSelectedVendor] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fetchControllerRef = useRef<AbortController | null>(null);

  const fetchInvoices = useCallback(
    async (overrides: { page?: number; perPage?: number; sort?: string; vendor?: string; invoiceNumber?: string }) => {
      const requestPage = overrides.page ?? page;
      const requestPerPage = overrides.perPage ?? perPage;
      const requestSort = overrides.sort ?? sort;
      const requestVendor = overrides.vendor ?? selectedVendor;
      const requestInvoiceNumber = overrides.invoiceNumber ?? searchInvoiceNumber;

      if (fetchControllerRef.current) fetchControllerRef.current.abort();
      const controller = new AbortController();
      fetchControllerRef.current = controller;

      setLoading(true);
      setError(null);
      try {
        const params: any = {
          page: requestPage,
          per_page: requestPerPage,
          sort: requestSort,
        };
        if (requestVendor) params.vendor_name = requestVendor;
        if (requestInvoiceNumber.trim()) params.invoice_number = requestInvoiceNumber.trim();
        const response: any = await axios.get('/vendors/servman_invoices', {
          params,
          signal: controller.signal,
        });
        setInvoices(response.data?.result || []);
        setTotalRecords(response.data?.total_records || 0);
        if (response.data?.vendor_names) setVendorNames(response.data.vendor_names);
      } catch (err: any) {
        if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
        setError('Failed to load ServMan invoices. Please try again.');
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    },
    [page, perPage, sort, selectedVendor, searchInvoiceNumber],
  );

  useEffect(() => {
    fetchInvoices({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = () => {
    setPage(1);
    fetchInvoices({ page: 1 });
  };

  const handleClearSearch = () => {
    setSearchInvoiceNumber('');
    setSelectedVendor('');
    setPage(1);
    fetchInvoices({ page: 1, vendor: '', invoiceNumber: '' });
  };

  const handleVendorChange = (value: string) => {
    setSelectedVendor(value);
    setPage(1);
    fetchInvoices({ page: 1, vendor: value });
  };

  return (
    <Grid container>
      <Grid item xs={12} style={{ marginTop: 20 }}>
        <Grid container justifyContent="space-between">
          <Grid item style={{ marginBottom: 3 }}>
            <Typography style={{ display: 'inline-block', fontSize: '20px' }} variant="h3">
              Vendor Management
            </Typography>
            <NavigateNextIcon
              fontSize="small"
              style={{ color: '#3d5a80', fontWeight: 700, fontSize: '20px', margin: '-4px 4px' }}
            />
            <Typography style={{ display: 'inline-block', fontSize: '20px' }} variant="h3">
              ServMan Invoices
            </Typography>
          </Grid>
        </Grid>
      </Grid>

      <Grid item xs={12} style={{ marginTop: 20 }}>
        <Card>
          <CardContent>
            {error && (
              <div style={{ color: 'red', marginBottom: 12 }}>{error}</div>
            )}
            <Grid container spacing={2} alignItems="center" style={{ marginBottom: 20 }}>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Search by Invoice Number"
                  value={searchInvoiceNumber}
                  onChange={(e) => setSearchInvoiceNumber(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  fullWidth
                  variant="outlined"
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth variant="outlined" size="small">
                  <InputLabel>Vendor Name</InputLabel>
                  <Select
                    value={selectedVendor}
                    onChange={(e) => handleVendorChange(e.target.value as string)}
                    label="Vendor Name"
                  >
                    <MenuItem value="">
                      <em>All Vendors</em>
                    </MenuItem>
                    {vendorNames.map((vendor) => (
                      <MenuItem key={vendor} value={vendor}>
                        {vendor}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item>
                <Button variant="contained" color="primary" startIcon={<SearchIcon />} onClick={handleSearch}>
                  Search
                </Button>
              </Grid>
              <Grid item>
                <Button variant="outlined" startIcon={<ClearIcon />} onClick={handleClearSearch}>
                  Clear
                </Button>
              </Grid>
            </Grid>
            <Box sx={{ mt: 3 }}>
              <DataTable
                title=""
                columns={invoiceColumns}
                data={invoices}
                pagination
                paginationServer
                paginationTotalRows={totalRecords}
                paginationPerPage={perPage}
                paginationRowsPerPageOptions={[10, 25, 50, 100]}
                onChangePage={(newPage: number) => {
                  setPage(newPage);
                  fetchInvoices({ page: newPage });
                }}
                onChangeRowsPerPage={(newPerPage: number) => {
                  setPerPage(newPerPage);
                  setPage(1);
                  fetchInvoices({ page: 1, perPage: newPerPage });
                }}
                onSort={(column: IDataTableColumn<any>, direction: 'asc' | 'desc') => {
                  const newSort = `${column.selector} ${direction}`;
                  setSort(newSort);
                  fetchInvoices({ sort: newSort });
                }}
                progressPending={loading}
                highlightOnHover
                pointerOnHover
                responsive
                dense
                noHeader
              />
            </Box>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default ServmanInvoicesListPage;
